import type {
  AdminHealth,
  GpuPoolState,
  ScaleDecision,
  Severity,
  TicketState,
} from './health-types';

export type HealthTone = 'success' | 'warning' | 'danger' | 'neutral';

export interface HealthSummary {
  tone: HealthTone;
  headline: string;
  critical: number;
  warn: number;
}

/** The answer in a word or two: is the watcher watching, and is anything open. */
export function healthSummary(view: AdminHealth): HealthSummary {
  const critical = view.alerts.open.filter((a) => a.severity === 'critical').length;
  const warn = view.alerts.open.filter((a) => a.severity === 'warn').length;
  const base = { critical, warn };
  if (view.watcher.lastLoopAt === null)
    return { ...base, tone: 'neutral', headline: 'Not reporting yet' };
  if (view.watcher.stale) return { ...base, tone: 'danger', headline: 'Watcher stopped' };
  if (critical > 0) return { ...base, tone: 'danger', headline: `${critical} critical` };
  if (warn > 0)
    return { ...base, tone: 'warning', headline: warn === 1 ? '1 warning' : `${warn} warnings` };
  return { ...base, tone: 'success', headline: 'All clear' };
}

export const severityTone: Record<Severity, HealthTone> = {
  critical: 'danger',
  warn: 'warning',
  info: 'neutral',
};

export const severityWord: Record<Severity, string> = {
  critical: 'Critical',
  warn: 'Warning',
  info: 'Info',
};

export const ticketWord: Record<TicketState, string> = {
  intent: 'Opening',
  created: 'Open',
  unknown: 'Open, unconfirmed',
  rejected: 'Refused by Desk',
  resolved: 'Closed',
};

export const ticketTone: Record<TicketState, HealthTone> = {
  intent: 'warning',
  created: 'warning',
  unknown: 'warning',
  rejected: 'danger',
  resolved: 'success',
};

export const poolStateWord: Record<GpuPoolState, string> = {
  on: 'On',
  starting: 'Starting',
  off: 'Off',
  unknown: 'Not read',
};

export const poolStateTone: Record<GpuPoolState, HealthTone> = {
  on: 'success',
  starting: 'warning',
  off: 'neutral',
  unknown: 'neutral',
};

/** What a decision did, in two or three words: Started, Would stop, Could not start. */
export function decisionWord(d: ScaleDecision): string {
  const start = d.to > d.from;
  if (d.outcome === 'dry-run') return start ? 'Would start' : 'Would stop';
  if (d.outcome === 'failed') return start ? 'Could not start' : 'Could not stop';
  return start ? 'Started' : 'Stopped';
}

export function decisionTone(d: ScaleDecision): HealthTone {
  if (d.outcome === 'failed') return 'danger';
  if (d.outcome === 'dry-run') return 'neutral';
  return d.to > d.from ? 'warning' : 'success';
}

/** How long something was open, from two instants, as the formatter's duration. */
export function openSeconds(from: string, until: string | number): number {
  const end = typeof until === 'number' ? until : Date.parse(until);
  return Math.max(0, Math.round((end - Date.parse(from)) / 1000));
}

/** The share of the day's card-hour cap used, 0 to 1 (more when past it); null without a cap. */
export function capShare(hours: number, cap: number | null): number | null {
  if (cap === null || cap <= 0) return null;
  return hours / cap;
}
